import type { AxiosError, AxiosResponse } from "axios";
import {
  AddNewCustomerUserNoteInput,
  BulkCreateCustomerUserInput,
  BulkDeleteCustomerUserInput,
  CreateCustomerUserInput,
  CreateCustomerUserOnboarding,
  CustomerUserCategory,
  CustomerUserSearchInput,
  GetAllCustomerUsersByMspIdInput,
  GetAllCustomerUsersInput,
  GetCustomerUserDocumentsInput,
  GetQueryProps,
  SetCustomerUserInput,
  UpdateCustomerUserCategoryInput,
  UpdateCustomerUserInput,
  UpdateCustomerUserNote,
} from "shared";
import { authAxiosClient, axiosClient } from "utilz";
import {
  apiErrorHandler,
  generateUrlString,
  getQueryDataObject,
} from "utilz/helpers";

export const customerUserSearch = async ({
  customerId,
  siteId,
  searchQuery,
}: CustomerUserSearchInput) => {
  try {
    const response = await axiosClient.get("/v1/customers/users/search", {
      params: {
        searchQuery,
        customerId,
        siteId,
        per_page: 2000,
        page: 1,
      },
    });
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error) {
    throw error;
  }
};
export const getAllCustomerUsers = async ({
  perPageCount = 1000,
  pageNumber,
  sortColumn,
  sortDirection = "asc",
  filter,
  searchQuery,
  customerId,
}: GetAllCustomerUsersInput) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    });

    const url = await generateUrlString({
      baseUrl: `/v2/customers/${customerId}/users`,
      data,
    });

    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error) {
    throw error;
  }
};

export const getAllCustomerUsersByMspId = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection = "asc",
  filter,
  searchQuery,
  mspId,
}: GetAllCustomerUsersByMspIdInput) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    });
    const url = await generateUrlString({
      baseUrl: "v2/servicedesk/customer_users/msp",
      data,
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response.data;
    if (resData.success && resData.statusCode === 200) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (error) {
    throw error;
  }
};

export const getUserDetails = async (userId: string) => {
  try {
    const url = `/v2/users/${userId}`;
    const response = await axiosClient.get(url);
    const resdata = response.data;
    if (resdata?.success && resdata?.statusCode === 200) {
      return resdata?.data;
    }
    throw resdata?.message;
  } catch (e) {
    throw e;
  }
};

export const getCustomerUserManagers = async (userId: string) => {
  try {
    const url = `/v2/users/${userId}/managers`;
    const response = await axiosClient.get(url);
    const resData = response?.data;
    if (
      resData?.statusCode === 200 &&
      resData?.success === true &&
      resData?.data
    ) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (e) {
    throw e;
  }
};

export const setCustomerUserManagers = async ({
  userId,
  customerId,
  managers,
}: SetCustomerUserInput) => {
  try {
    const url = `/v2/customers/${customerId}/managers`;
    const data = {
      customer_user_id: userId,
      managers,
    };
    const response = await axiosClient.post(url, data);
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (e) {
    throw e;
  }
};

export const createCustomerUser = async ({
  customerId,
  data,
}: CreateCustomerUserInput) => {
  try {
    const url = `/v2/customers/${customerId}/users`;
    const response = await axiosClient.post(url, data);
    const resData = response?.data;
    if (resData.statusCode === 200 && resData.success === true) {
      return resData;
    }
    throw resData?.message;
  } catch (e) {
    throw e;
  }
};

export const createCustomerUserOnBoarding = async ({
  customerId,
  users,
  send_invite,
}: CreateCustomerUserOnboarding) => {
  const modData = {
    users: users,
    send_invite: send_invite,
  };
  try {
    const url = `/v2/customers/users/onboarding/batch/${customerId}`;
    const response = await axiosClient.post(url, modData);
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return resData;
    }
    throw resData?.message;
  } catch (e) {
    throw e;
  }
};

export const getCustomerUserCategories = async (): Promise<
  CustomerUserCategory[]
> => {
  try {
    const response = await axiosClient.get("/configuration/user-categories");
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return Array.isArray(resData?.data?.result) ? resData.data.result : [];
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

/**
 * Rename one customer-user category.
 *
 * The name is the ONLY mutable field — `mapping_id` and `level` are the backend's
 * identity/ordering keys, which is why the crown tiers are derived from `level`
 * and not from the label (see CategorySelectField.tsx).
 */
export const updateCustomerUserCategory = async ({
  mappingId,
  name,
}: UpdateCustomerUserCategoryInput) => {
  try {
    const url = `/configuration/user-categories/${mappingId}`;
    const response = await axiosClient.patch(url, { name });
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return resData;
    }
    throw response;
  } catch (error) {
    return apiErrorHandler(error as AxiosError | AxiosResponse);
  }
};

export const updateCustomerUser = async ({
  customerUserId,
  data,
}: UpdateCustomerUserInput) => {
  try {
    const url = `/v2/users/${customerUserId}`;
    const response = await axiosClient.patch(url, data);
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return resData;
    }
    throw resData?.message;
  } catch (e) {
    throw e;
  }
};

export const updateCustomerUserStatus = async ({
  customerUserId,
  isActive,
}: {
  customerUserId: string;
  isActive: boolean;
}) => {
  try {
    const url = `/v1/customers/users/${customerUserId}/active/${isActive}`;
    const response = await axiosClient.patch(url);
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success) {
      return resData;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const updateCustomerUserProfilePhoto = async ({
  customerUserId,
  file,
}: {
  customerUserId: string;
  file: any;
}) => {
  try {
    const url = `/v2/users/${customerUserId}/photo`;
    const response = await axiosClient.post(url, file, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return resData;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const removeCustomerUserProfilePhoto = async ({
  customerUserId,
}: {
  customerUserId: string;
}) => {
  try {
    const url = `/v2/users/${customerUserId}/photo`;
    const response = await axiosClient.delete(url, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return resData;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const bulkDeleteCustomerUsers = async ({
  userIds,
}: BulkDeleteCustomerUserInput) => {
  try {
    const url = "v2/customer/users";

    const data = {
      customer_user_ids: userIds,
    };

    const response = await axiosClient.delete(url, { data });
    const resData = response.data;
    if (resData.statusCode === 200 && resData.success === true) {
      return resData.data;
    } else {
      throw new Error(resData?.message);
    }
  } catch (e) {
    throw e;
  }
};

export const addBulkUsersToCustomer = async ({
  customerId,
  file,
  send_invite,
}: BulkCreateCustomerUserInput) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("send_invite", String(send_invite || false));
    const url = `/v2/bulk/invite/users/${customerId}`;
    const response = await axiosClient.post(url, formData);
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success === true) {
      return resData?.data;
    } else {
      throw resData;
    }
  } catch (e) {
    throw e;
  }
};

//NOTES
type GetQueryPropsWithCustomerUserId = GetQueryProps & {
  customerUserId: string;
};
export const getCustomerUserNotes = async ({
  perPageCount = 100,
  pageNumber = 1,
  sortColumn,
  sortDirection = "asc",
  filter,
  searchQuery,
  customerUserId,
}: GetQueryPropsWithCustomerUserId) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    });
    const url = await generateUrlString({
      baseUrl: `/v2/users/${customerUserId}/notes`,
      data,
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    } else {
      return resData?.message;
    }
  } catch (error) {
    throw error;
  }
};

export const addNewCustomerUserNote = async ({
  customerUserId,
  noteData,
}: AddNewCustomerUserNoteInput) => {
  try {
    const url = `/v2/users/${customerUserId}/notes`;
    const response = await axiosClient.post(url, noteData);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const updateCustomerUserNote = async ({
  noteId,
  customerUserId,
  noteData,
}: UpdateCustomerUserNote) => {
  try {
    const url = await generateUrlString({
      baseUrl: `/v2/notes/${noteId}`,
      data: { customer_user_id: customerUserId },
    });
    const response = await axiosClient.patch(url, noteData);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw response;
  } catch (error: any) {
    apiErrorHandler(error);
  }
};

export const deleteBulkCustomerUserNotes = async ({
  noteIds,
}: {
  noteIds: string[];
}) => {
  try {
    const data = { note_ids: [...noteIds] };
    const url = "/v2/notes/users";
    const response = await axiosClient.put(url, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const addCustomerUserDocument = async ({
  customerUserId,
  formData,
}: {
  customerUserId: string;
  formData: any;
}) => {
  try {
    const url = `/v2/users/${customerUserId}/attachments`;
    const response = await axiosClient.post(url, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const editCustomerUserDocumentDescription = async ({
  attachment_id,
  data,
}: {
  attachment_id: string;
  data: any;
}) => {
  try {
    const url = `/v2/attachments/${attachment_id}/description`;
    const response = await axiosClient.patch(url, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const getCustomerUserDocuments = async ({
  perPageCount,
  pageNumber,
  sortColumn,
  sortDirection = "asc",
  filter,
  searchQuery,
  customerUserId,
}: GetCustomerUserDocumentsInput) => {
  try {
    const data = await getQueryDataObject({
      perPageCount,
      pageNumber,
      sortColumn,
      sortDirection,
      filter,
      searchQuery,
    });
    const url = await generateUrlString({
      baseUrl: `/v2/users/${customerUserId}/attachments`,
      data,
    });
    const response = await axiosClient.get(`${url}`);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const deleteBulkCustomerUserDocuments = async ({
  attachmentIds,
}: {
  attachmentIds: string[];
}) => {
  const data = { attachment_ids: [...attachmentIds] };
  try {
    const url = "/v2/attachments";
    const response = await axiosClient.patch(url, data);
    const resData = response?.data;
    if (resData?.success && resData?.statusCode === 200) {
      return resData?.data;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const resetCustomerUserPassword = async (email: string) => {
  try {
    const url = "/reset/user/password/token";
    const response = await authAxiosClient.post(url, { email });
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success) {
      return resData;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};

export const bulkUpdateCustomerUsersManagerOrSite = async ({
  type,
  typeId,
  userIds,
}: {
  type: "manager" | "site";
  typeId: string;
  userIds: string[];
}) => {
  try {
    const url = `/v1/users/${type}/${typeId}`;
    const response = await axiosClient.patch(url, userIds);
    const resData = response?.data;
    if (resData?.statusCode === 200 && resData?.success) {
      return resData;
    }
    throw resData?.message;
  } catch (error) {
    throw error;
  }
};
